import { Breadcrumb } from 'antd';
import { createSonarSelector } from '@lib/selectors';
import { getBreadcrumbs } from '@sonar-software/sonar-ts-lib';
import React from 'react';

import {
	BASE_ROUTES,
	COPY_SPECIFIC,
	FULL_ROUTES,
	SETTINGS_DATA_SECURITY_COPY
} from '@sonar-software/sonar-ts-constants';
import { NavLink, useLocation } from 'react-router-dom';
import { useCurrentInitiative, useDataSecurityApps, useSonarSelector } from '@hooks';

const routeDisplayLookup: Record<string, string> = {
	[ BASE_ROUTES.ISSUES ]: COPY_SPECIFIC.VALIDATION_RULE_ISSUES,
	[ BASE_ROUTES.DATA_SECURITY ]: COPY_SPECIFIC.APPLICATION_OVERVIEW,
	[ BASE_ROUTES.API_VOLUME ]: COPY_SPECIFIC.API_VOLUME_EXPLORER,
	[ BASE_ROUTES.SYNC ]: COPY_SPECIFIC.METADATA_IMPORT
};

const dataSecuritySafeFullRoutes = [
	FULL_ROUTES.DATA_SECURITY_DATA_ACCESS
];

const periscopesSelector = createSonarSelector((state) => state.application.periscope?.cards);

export const BreadcrumbHeader: React.FC = () => {
	const { pathname } = useLocation();

	const dataAccessUrl =
		new RegExp(
			`(^.*(${BASE_ROUTES.ACCESS_AUDIT}|${BASE_ROUTES.ACCESS_EXPLAINER}|${BASE_ROUTES.ACCESS_COMPARISON})).*$`,
			'u'
		);

	const pathNameTrimed = pathname.replace(dataAccessUrl, '$1');

	const apps = useDataSecurityApps(false, true);
	const currentInitiative = useCurrentInitiative();
	const periscopes = useSonarSelector(periscopesSelector);

	if (pathNameTrimed === '/' || pathNameTrimed === FULL_ROUTES.DASHBOARD) {
		return (
			<Breadcrumb
				items={ [ {
					key: COPY_SPECIFIC.DASHBOARD,
					title: COPY_SPECIFIC.DASHBOARD
				} ] } />
		);
	}

	if (pathNameTrimed.includes('periscope') && periscopes.length) {
		return (
			<Breadcrumb
				items={ periscopes.map((scope) => ({
					key: scope.id,
					title: scope.name
				})) } />
		);
	}

	const breadcrumbsMap = getBreadcrumbs(pathNameTrimed, routeDisplayLookup);
	const breadcrumbEntries = Object.entries(breadcrumbsMap);

	const forDataSecurity = breadcrumbEntries[ 0 ][ 0 ] === FULL_ROUTES.DATA_SECURITY;
	const forDataSecurityRoot = forDataSecurity && breadcrumbEntries.length === 1;
	const shouldStripDataSecurity = forDataSecurity && !forDataSecurityRoot &&
		!dataSecuritySafeFullRoutes.includes(breadcrumbEntries[ 1 ][ 0 ]);

	if (shouldStripDataSecurity) {
		const [ , [ , target ] ] = breadcrumbEntries;

		if (isNaN(Number(target))) {
			breadcrumbEntries.shift();
		}
	}

	if (forDataSecurity && !shouldStripDataSecurity) {
		breadcrumbEntries[ 0 ][ 1 ] = COPY_SPECIFIC.DATA_SECURITY;
	}

	return (
		<Breadcrumb
			itemRender={ (route) => (route.path ? <NavLink to={ route.path }>{ route.title }</NavLink> : route.title) }
			items={ breadcrumbEntries.map((value) => {
				const [ link, text ] = value;
				const fullRoute = link.slice(1).split('/');
				const id = Number(fullRoute[ fullRoute.length - 1 ]);
				const route = fullRoute[ fullRoute.length - 2 ];

				let displayText = text;

				if (id && route === BASE_ROUTES.INITIATIVES) {
					displayText = currentInitiative?.title ?? '';
				} else if (id && route === BASE_ROUTES.APPLICATION_SETUP) {
					const name = apps.find((app) => app.applicationId === id)?.name ?? '';

					displayText = SETTINGS_DATA_SECURITY_COPY.MONITOR_SETTINGS(name);
				} else if (id && route === BASE_ROUTES.DATA_SECURITY) {
					displayText = apps.find((app) => app.applicationId === id)?.name ?? '';
				}

				const isLast = value === breadcrumbEntries[ breadcrumbEntries.length - 1 ];

				return {
					key: text,
					path: isLast ? undefined : link,
					title: displayText
				};
			}) } />
	);
};

